import clsx from "clsx";

export interface ProgressBarProps {
  /** 0–1. Values outside the range are clamped. */
  value: number;
  label?: string;
  /** Accessible name when no visible `label` is rendered (e.g. the label sits outside the bar). */
  ariaLabel?: string;
  /**
   * What a screen reader announces instead of the bare number (`aria-valuetext`),
   * e.g. a localized "60 %", or "ej bedömd" for a bar with nothing to show yet.
   */
  valueText?: string;
  /** Text shown to the right of the bar, e.g. "3 / 7". */
  caption?: string;
  /**
   * Hide `caption` from assistive tech (`aria-hidden`) when it just repeats
   * what `ariaLabel`/`label` and `valueText` already announce on the bar
   * itself, so a screen reader doesn't hear the same words twice.
   */
  captionHidden?: boolean;
  tone?: "accent" | "success" | "warning" | "muted";
  /** `sm` is the thin secondary bar, e.g. the amount of evidence under an accuracy bar. */
  size?: "md" | "sm";
  className?: string;
}

const TONES = {
  accent: "bg-accent-600",
  success: "bg-green-600",
  warning: "bg-amber-500",
  muted: "bg-ink-500 dark:bg-ink-400",
};

const SIZES = {
  md: "h-2",
  sm: "h-1",
};

export function ProgressBar({
  value,
  label,
  ariaLabel,
  valueText,
  caption,
  captionHidden = false,
  tone = "accent",
  size = "md",
  className,
}: ProgressBarProps) {
  const pct = Math.round(Math.min(1, Math.max(0, value)) * 100);
  return (
    <div className={clsx("flex flex-col gap-1", className)}>
      {(label || caption) && (
        <div className="flex items-baseline justify-between text-xs text-ink-600 dark:text-ink-400">
          {label && <span>{label}</span>}
          {caption && (
            <span className="tabular-nums" aria-hidden={captionHidden ? "true" : undefined}>
              {caption}
            </span>
          )}
        </div>
      )}
      <div
        role="progressbar"
        aria-valuenow={pct}
        aria-valuemin={0}
        aria-valuemax={100}
        aria-valuetext={valueText}
        aria-label={ariaLabel ?? label}
        className={clsx(
          "w-full overflow-hidden rounded-full bg-ink-200 dark:bg-ink-800",
          SIZES[size],
        )}
      >
        <div
          className={clsx("h-full rounded-full transition-all", TONES[tone])}
          style={{ width: `${pct}%` }}
        />
      </div>
    </div>
  );
}
